import { useMemo, useCallback } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Api } from '../../../../../generated/Api';
import { config as appConfig } from '../../../../../config/config';
import { EntityType } from '../../../../../generated/data-contracts';
import { authService } from '../../../../../services/authService';

export const useRolePermissionsApi = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Crear instancia de la API
  const api = useMemo(() => {
    const apiInstance = new Api({
      baseURL: appConfig.apiUrl,
    });

    // Configurar interceptors para incluir el token - ✅ UNIFICADO
    apiInstance.instance.interceptors.request.use(configReq => {
      const token = authService.getAccessToken();
      if (token) {
        configReq.headers.Authorization = `Bearer ${token}`;
      }
      return configReq;
    });

    return apiInstance;
  }, []);

  // Función para cargar todas las páginas usando dataProvider genérico
  const loadAllPages = useCallback(
    async (
      resource: string,
      filter: any = {},
      sort: any = { field: 'id', order: 'ASC' }
    ) => {
      let allData: any[] = [];
      let currentPage = 1;
      const perPage = 100;

      try {
        while (true) {
          const response = await dataProvider.getList(resource, {
            pagination: { page: currentPage, perPage },
            sort,
            filter,
          });

          allData = [...allData, ...response.data];

          // Si obtuvimos menos elementos que el perPage, ya no hay más páginas
          if (response.data.length < perPage) {
            break;
          }

          currentPage++;
        }

        return allData;
      } catch (error) {
        console.error(`Error loading ${resource}:`, error);
        throw error;
      }
    },
    [dataProvider]
  );

  // Función para filtrar entidades que ya tienen permisos
  const filterExistingPermissions = useCallback(
    (entities: any[], entityType: EntityType, existingPermissions: any[]) => {
      const existingEntityIds = existingPermissions
        .filter(permission => permission.entityType === entityType)
        .map(permission => permission.entityId);

      return entities.filter(entity => !existingEntityIds.includes(entity.id));
    },
    []
  );

  // Función para eliminar un permiso
  const deletePermission = useCallback(
    async (permissionId: string | number) => {
      return await api.rolePermissionsDelete(Number(permissionId));
    },
    [api]
  );

  // Función para actualizar un permiso
  const updatePermission = useCallback(
    async (permission: any) => {
      return await api.rolePermissionsUpdate(permission);
    },
    [api]
  );

  return {
    api,
    dataProvider,
    notify,
    loadAllPages,
    filterExistingPermissions,
    deletePermission,
    updatePermission,
  };
};
